import React, { useState } from "react";
import {
  ChevronUp,
  ChevronDown,
  X,
  ShoppingCart,
  Check,
  Heart,
  Gift,
  ShieldCheck,
  CalendarDays,
} from "lucide-react";
import { configuratorData, detailedProducts } from "../data/data";

const ConfiguratorSection = ({ navigateTo, onOrder, favorites = [], productsData = {}, currentStep, setCurrentStep }) => {
  const [selectedConfig, setSelectedConfig] = useState({
    motor: null,
    battery: null,
    display: null,
    brakes: null,
    assembly: null,
  });
  const [openSections, setOpenSections] = useState({
    motor: true,
    battery: false,
    display: false,
    brakes: false,
    assembly: false,
  });

  // --- CHYTRÉ ZÍSKÁNÍ REÁLNÉ CENY PRO ŘAZENÍ ---
  const getRealPrice = (item) => {
    let price = productsData[item.id] ? productsData[item.id].price : item.price;
    if (item.name?.includes("dílně") || item.name?.includes("u nás")) price = 1500;
    if (item.name?.includes("doma") || item.name?.includes("u vás")) price = 3000;
    if (item.id === "ass_none" || item.id === "asm_none" || item.name?.includes("Bez montáže")) price = 0;
    return Number(price) || 0;
  };

  // Základní řazení podle reálné ceny (od nejmenší)
  const sortByLivePrice = (a, b) => getRealPrice(a) - getRealPrice(b);

  // Chytré řazení pro baterie (Volty -> Cena)
  const sortBatteries = (a, b) => {
    if (a.id === "bat_none") return -1; // "Bez baterie" vždy první
    if (b.id === "bat_none") return 1;

    const getVolts = (name) => {
      if (!name) return 999;
      const match = name.match(/(\d+)\s*v/i);
      return match ? parseInt(match[1]) : 999;
    };

    const vA = getVolts(a.name);
    const vB = getVolts(b.name);

    if (vA !== vB) {
      return vA - vB; // Nejdřív 36V, pak 48V
    }
    return getRealPrice(a) - getRealPrice(b); // V rámci napětí seřadí podle ceny
  };

  const fbMotors = Object.values(productsData).filter(p => p.type === "motor" && !p.isHidden).sort(sortByLivePrice);
  const fbBatteries = Object.values(productsData).filter(p => p.type === "battery" && !p.isHidden);
  const fbDisplays = Object.values(productsData).filter(p => p.type === "display" && !p.isHidden).sort(sortByLivePrice);
  const fbBrakes = Object.values(productsData).filter(p => p.type === "brake" && !p.isHidden).sort(sortByLivePrice);

  const motorsList = fbMotors;
  
  const batNone = { id: "bat_none", name: "Bez baterie", price: 0, image: null, desc: "Sada bez baterie" };
  const batteriesList = [batNone, ...fbBatteries].sort(sortBatteries);
  
  const displaysList = fbDisplays;
  const brakesList = fbBrakes;
  
  // Nyní už se i montáž správně seřadí zleva doprava podle skutečné ceny!
  const assemblyList = [...configuratorData.assembly].sort(sortByLivePrice); 

  const isUnlocked = (section) => {
    if (section === "motor") return true;
    if (section === "battery") return selectedConfig.motor !== null;
    if (section === "display") return selectedConfig.battery !== null;
    if (section === "brakes") return selectedConfig.display !== null;
    if (section === "assembly") return selectedConfig.brakes !== null;
    return false;
  };

  const toggleSection = (section) => {
    if (!isUnlocked(section)) return;
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const selectItem = (category, item, priceOverride) => {
    setSelectedConfig((prev) => {
      const itemWithLivePrice = { ...item, price: priceOverride };

      const newConfig = { ...prev, [category]: itemWithLivePrice };
      if (
        category === "motor" &&
        newConfig.battery &&
        newConfig.battery.id !== "bat_none"
      ) {
        const is36V = newConfig.battery.name.includes("36V") || newConfig.battery.name.includes("36 V");
        const is250W = item.name.includes("250W") || item.name.includes("250 W") || item.id === "mot_250";
        
        if (is250W && !is36V) newConfig.battery = null;
        if (!is250W && is36V) newConfig.battery = null;
      }
      return newConfig;
    });

    const order = ["motor", "battery", "display", "brakes", "assembly"];
    const currentIndex = order.indexOf(category);
    if (currentIndex < order.length - 1) {
      const nextCategory = order[currentIndex + 1];
      setOpenSections((prev) => ({
        ...prev,
        [category]: false,
        [nextCategory]: true,
      }));
      if (setCurrentStep) setCurrentStep(currentIndex + 2);
    }
  };

  const subtotal =
    (selectedConfig.motor?.price || 0) +
    (selectedConfig.battery?.price || 0) +
    (selectedConfig.display?.price || 0) +
    (selectedConfig.brakes?.price || 0) +
    (selectedConfig.assembly?.price || 0);

  const hasSetDiscount = 
    selectedConfig.motor && 
    selectedConfig.battery && 
    selectedConfig.battery.id !== "bat_none" && 
    selectedConfig.display;
    
  const discount = hasSetDiscount ? 2000 : 0;
  const totalPrice = subtotal - discount;

  const isComplete =
    selectedConfig.motor &&
    selectedConfig.battery &&
    selectedConfig.display &&
    selectedConfig.brakes &&
    selectedConfig.assembly;

  const getFilteredData = (key, data) => {
    if (key === "battery" && selectedConfig.motor) {
      return data.filter((bat) => {
        if (bat.id === "bat_none") return true;
        const is36V = bat.name.includes("36V") || bat.name.includes("36 V");
        const is250W = selectedConfig.motor.name.includes("250W") || selectedConfig.motor.name.includes("250 W") || selectedConfig.motor.id === "mot_250";
        
        return is250W ? is36V : !is36V;
      });
    }
    return data;
  };

  const getMotorSubtitle = (name) => {
    if (name.includes("250W")) return "Ideální volba do města";
    if (name.includes("750W") || name.includes("500W"))
      return "Nejlepší poměr cena/výkon";
    if (name.includes("1000W") || name.includes("TSDZ16"))
      return "Absolutní bestie";
    return null;
  };

  const getLegality = (category, item) => {
    if (item.id === "bat_none") return null;
    return { legal: true, text: "SPLŇUJE LEGISLATIVU" };
  };

  return (
    <div
      id="configurator"
      className="max-w-5xl mx-auto px-4 py-12 scroll-mt-24 relative"
    >
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-bold text-[#2a3b4c]">
          Spočítejte si cenu přestavby
        </h2>
        <p className="text-slate-400 mt-2 text-lg">
          Všechny naše sady jsou <span className="text-green-600 font-bold uppercase tracking-tight">plně legální</span> pro provoz na komunikacích.
        </p>
      </div>

      <div className="space-y-4">
        {[
          { key: "motor", title: "1. Výběr motoru", data: motorsList },
          {
            key: "battery",
            title: "2. Kapacita baterie",
            data: getFilteredData("battery", batteriesList),
          },
          {
            key: "display",
            title: "3. Typ displeje",
            data: displaysList,
          },
          {
            key: "brakes",
            title: "4. Brzdové senzory",
            data: brakesList,
          },
          {
            key: "assembly",
            title: "5. Způsob montáže",
            data: assemblyList,
          },
        ].map((section) => (
          <div
            key={section.key}
            className="bg-white rounded-xl border border-slate-200 overflow-hidden"
          >
            <button
              onClick={() => toggleSection(section.key)}
              className={`w-full flex items-center justify-between p-5 transition-colors text-left ${
                !isUnlocked(section.key)
                  ? "text-slate-400 cursor-not-allowed"
                  : "text-[#2a3b4c] hover:bg-slate-50"
              }`}
            >
              <span className="font-bold text-base md:text-xl flex flex-wrap items-center gap-1 md:gap-3">
                {section.title}
                {selectedConfig[section.key] && (
                  <span className="text-green-600 text-sm md:text-lg font-bold bg-green-50 px-3 py-1 rounded-lg border border-green-100">
                    {selectedConfig[section.key].name}
                  </span>
                )}
              </span>
              {openSections[section.key] ? (
                <ChevronUp className="w-6 h-6 shrink-0 ml-2" />
              ) : (
                <ChevronDown className="w-6 h-6 shrink-0 ml-2" />
              )}
            </button>

            {openSections[section.key] &&
              isUnlocked(section.key) &&
              (section.key === "assembly" ? (
                <div className="p-4 md:p-6 border-t border-slate-100 flex flex-col gap-4 bg-slate-50/30">
                  {section.data.map((item) => {
                    const isSelected = selectedConfig[section.key]?.id === item.id;
                    const livePrice = getRealPrice(item); // Používá naši novou chytrou funkci

                    return (
                      <div
                        key={item.id}
                        onClick={() => selectItem(section.key, item, livePrice)}
                        className={`p-5 rounded-xl cursor-pointer transition flex flex-col md:flex-row justify-between items-start md:items-center border bg-white ${
                          isSelected
                            ? "border-green-500 ring-2 ring-green-500/20 shadow-md"
                            : "border-slate-100 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex-1">
                          <h4 className="font-bold text-[#2a3b4c] text-lg">{item.name}</h4>
                          <p className="text-sm text-slate-500 mt-1 italic">
                            {item.name.includes("dílně") || item.name.includes("u nás")
                              ? "Kolo přivezete k nám do dílny. Po odeslání objednávky vás kontaktujeme pro domluvení termínu." 
                              : item.name.includes("doma") || item.name.includes("u vás")
                              ? "Přijedeme přímo za vámi a přivezeme veškeré potřebné nářadí. Termín si domluvíme telefonicky po objednávce."
                              : item.desc}
                          </p>
                        </div>
                        <div className="flex flex-col items-start md:items-end mt-4 md:mt-0 md:ml-6 shrink-0 w-full md:w-auto">
                          <div className="font-black text-[#2a3b4c] text-xl whitespace-nowrap">
                            {livePrice === 0 ? "Zdarma" : `${livePrice.toLocaleString()} Kč`}
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); selectItem(section.key, item, livePrice); }}
                            className={`mt-3 px-6 py-2 font-bold text-sm rounded-md transition-colors w-full md:w-auto flex items-center justify-center gap-2 ${
                              isSelected
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-green-600 hover:bg-green-700 text-white"
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4" />}
                            {isSelected ? "Vybráno" : "Vybrat"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 md:p-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 bg-slate-50/30">
                  {section.data.map((item) => {
                    const detailedProduct = detailedProducts[item.id];
                    const isSelected = selectedConfig[section.key]?.id === item.id;
                    
                    const imageUrl = item.images?.[0] || item.image || (detailedProduct && (detailedProduct.image || (detailedProduct.images && detailedProduct.images[0])));
                    
                    const motorSubtitle = section.key === "motor" ? getMotorSubtitle(item.name) : null;
                    const legality = getLegality(section.key, item);
                    
                    const livePrice = getRealPrice(item); // Používá naši novou chytrou funkci

                    return (
                      <div
                        key={item.id}
                        onClick={() => selectItem(section.key, item, livePrice)}
                        className={`p-5 rounded-2xl cursor-pointer transition flex flex-col items-center text-center border bg-white h-full relative group ${
                          isSelected ? "border-green-500 ring-2 ring-green-500/20 shadow-lg" : "border-slate-100 hover:border-slate-300"
                        }`}
                      >
                        <div className="w-full h-32 md:h-40 mb-4 flex items-center justify-center bg-slate-50 rounded-xl overflow-hidden relative">
                          {legality && (
                            <div className="absolute top-2 left-2 px-2 py-1 rounded text-[9px] font-black flex items-center gap-1 shadow-sm border bg-green-50 z-10 text-green-700 border-green-200 uppercase tracking-wide">
                              <ShieldCheck className="w-3 h-3 text-green-600" />
                              {legality.text}
                            </div>
                          )}
                          {favorites.includes(item.id) && (
                            <div className="absolute top-2 right-2 bg-white/80 p-1.5 rounded-full shadow-sm z-10 border border-red-100">
                              <Heart className="w-4 h-4 text-red-500 fill-current" />
                            </div>
                          )}
                          {item.id === "bat_none" ? (
                            <X className="w-20 h-20 text-red-500 stroke-[3]" />
                          ) : imageUrl ? (
                            <img src={imageUrl} alt={item.name} className="max-h-full max-w-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105" />
                          ) : (
                            <div className="w-full h-full bg-slate-50 text-slate-300 text-xs flex items-center justify-center text-center">Bez fotky</div>
                          )}
                        </div>
                        <h4 className="font-bold text-base text-[#2a3b4c] mb-1">{item.name}</h4>
                        {motorSubtitle && (
                          <div className="text-[11px] font-black text-amber-500 mb-2 uppercase tracking-wide bg-amber-50 px-2 py-0.5 rounded">
                            {motorSubtitle}
                          </div>
                        )}
                        <div className="font-black text-xl text-green-600 mb-2">
                          {livePrice === 0 ? "Zdarma" : `${livePrice.toLocaleString()} Kč`}
                        </div>
                        <div className="flex flex-col xl:flex-row gap-2 mt-auto w-full pt-4 border-t border-slate-50">
                          {detailedProduct && item.id !== "bat_none" && (
                            <button
                              onClick={(e) => { e.stopPropagation(); navigateTo("productDetail", item.id); }}
                              className="flex-1 py-2 px-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-md transition-colors w-full"
                            >
                              Zjistit více
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); selectItem(section.key, item, livePrice); }}
                            className={`flex-1 py-2 px-2 font-bold text-sm rounded-md transition-colors flex items-center justify-center gap-1.5 w-full ${
                              isSelected
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-green-600 hover:bg-green-700 text-white"
                            }`}
                          >
                            {isSelected && <Check className="w-4 h-4" />}
                            {isSelected ? "Vybráno" : "Vybrat"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* FINÁLNÍ SUMAČ SE SLEVOU */}
      <div className="mt-12 p-8 md:p-12 bg-gradient-to-b from-green-50 to-white rounded-3xl flex flex-col items-center justify-center text-center border-2 border-green-100 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-4 text-green-100 rotate-12">
            <Gift size={120} />
        </div>
        <h3 className="text-2xl md:text-3xl font-black text-[#2a3b4c] mb-6 relative z-10">
          Vaše výsledná cena
        </h3>
        <div className="space-y-3 mb-10 relative z-10">
          <div className="text-slate-400 text-xl flex items-center justify-center gap-4 font-bold">
              Původní cena: <span className="line-through">{subtotal.toLocaleString()} Kč</span>
          </div>
          {hasSetDiscount && (
            <div className="inline-flex items-center gap-3 bg-red-600 text-white px-6 py-2 rounded-2xl font-black text-xl animate-bounce shadow-lg">
              <Gift className="w-6 h-6" /> SETOVÁ SLEVA: -2 000 Kč
            </div>
          )}
          <div className="text-6xl md:text-7xl font-black text-[#2a3b4c] pt-4 leading-none">
            {totalPrice.toLocaleString()} <span className="text-3xl md:text-4xl">Kč</span>
          </div>
        </div>

        {selectedConfig.assembly && selectedConfig.assembly.id !== "ass_none" && selectedConfig.assembly.id !== "asm_none" && (
            <div className="mb-6 flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-xl border border-blue-100 font-bold animate-in fade-in zoom-in duration-500">
              <CalendarDays size={20} /> Po objednávce vás budeme kontaktovat pro domluvení termínu montáže.
            </div>
        )}

        <button
          onClick={() => isComplete && onOrder(selectedConfig)}
          disabled={!isComplete}
          className={`px-16 py-5 rounded-2xl font-black text-xl transition-all duration-300 flex items-center gap-4 shadow-2xl relative z-10 ${
            isComplete
              ? "bg-green-600 hover:bg-green-700 text-white hover:scale-105 active:scale-95"
              : "bg-slate-200 text-slate-400 cursor-not-allowed"
          }`}
        >
          <ShoppingCart className="w-8 h-8" /> PŘIDAT KOMPLETNÍ SET DO KOŠÍKU
        </button>
        {!isComplete && (
          <div className="mt-6 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-2xl border border-amber-200 shadow-sm relative z-10">
            <p className="text-amber-700 font-bold text-base flex items-center gap-2">
              ☝️ Pro získání slevy 2 000 Kč vyberte Motor, Baterii a Displej
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfiguratorSection;